// src/components/PatientLayout.js
// Patient-facing AppShell — mirrors Layout.js exactly.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  profileGateStatus,
  isSymptomLogComplete,
  firstIncompleteAnchor,
  HEALTH_PROFILE_ANCHOR,
  TOTAL_SYMPTOM_QUESTIONS,
} from '../utils/patientProfileCompleteness';
import { usePatient } from '../context/PatientContext';
import NotificationBell from './NotificationBell';
import AccountMenu from './AccountMenu';
import ThemeToggle from './ThemeToggle';
import api from '../services/api';
import avatarTint from '../utils/avatarTint';
import Icon from './Icon';
import previuLogo from '../assets/previu-logo-white.png';

function planChipTone(plan) {
  if (plan.guardianDubai) return 'badge-purple';
  return plan.isPaid ? 'badge-active' : 'badge-pending';
}

function personInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

function NavigatorCard({ careTeam, careNavigator, onClose }) {
  if (careTeam === null) return null;

  // ?navigator=1 tells PatientCareTeamPage to open the navigator's thread
  // rather than its default of the most recently active conversation — the
  // card says "Message →", so it should land on the navigator. With nobody
  // assigned there is no thread to open, so it stays a plain link.
  return (
    <NavLink
      to={careNavigator ? '/patient/care-team?navigator=1' : '/patient/care-team'}
      onClick={onClose}
      className="sidebar-person-card"
    >
      <div className="sidebar-person-card-label">Your navigator</div>
      {careNavigator ? (
        <div className="sidebar-person-card-row">
          <div className={`avatar avatar--sm ${avatarTint(careNavigator.name)}`} aria-hidden="true">
            {personInitials(careNavigator.name)}
          </div>
          <div className="sidebar-person-card-body">
            <div className="sidebar-person-card-name">{careNavigator.name}</div>
            <div className="sidebar-person-card-meta">Message →</div>
          </div>
        </div>
      ) : (
        <div className="sidebar-person-card-meta">
          Being assigned — see your care team →
        </div>
      )}
    </NavLink>
  );
}

// Bottom tab bar — mobile only. No sign-out tab here, unlike the doctor bar
// — that one's own tab list already fits comfortably at 3 items plus
// sign-out; a patient's is deliberately just the most-used screens.
// Assistant added per the Figma reference for the (now-default, LIGHT-mode)
// Ask Previu screen, which puts it in the bottom bar rather than
// leaving it reachable only via the hamburger sidebar — it's the landing
// screen for most patients now, not a secondary one.
const PATIENT_BOTTOM_TABS = [
  { to: '/patient/today',     icon: 'compass', label: 'Today' },
  { to: '/patient/chat',      icon: 'chat', label: 'Ask Previu' },
  { to: '/patient/care-plan', icon: 'clipboard', label: 'Care plan' },
  { to: '/patient/care-team', icon: 'stethoscope', label: 'Care Team' },
];

// Lab Tests and My Care Plan are no longer their own sidebar entries — they
// live as tabs under My Appointments (AppointmentsTabs) and My Health
// (MyHealthTabs) respectively, reached via those pages instead.
const PATIENT_NAV = [
  { to: '/patient/today',        icon: 'compass', label: 'Today',           section: null     },
  { to: '/patient/chat',         icon: 'chat', label: 'Ask Previu',  section: null     },
  { to: '/patient/timeline',     icon: 'trend', label: 'My Health Story',   section: 'Health', alsoActiveOn: ['/patient/care-plan'] },
  { to: '/patient/records',      icon: 'folder', label: 'My Documents',      section: 'Health' },
  { to: '/patient/care-team',    icon: 'stethoscope', label: 'Care Team',         section: 'Health' },
  { to: '/patient/appointments', icon: 'calendar', label: 'My Appointments',   section: 'Health', alsoActiveOn: ['/patient/lab-tests', '/patient/visits'] },
];

function PlanBlock({ plan }) {
  if (!plan || plan.loading) return null;
  return (
    <NavLink
      to="/patient/subscription"
      style={{
        display: 'block', padding: '11px 16px', textDecoration: 'none',
        borderBottom: '1px solid var(--bg-canvas)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className={`badge ${planChipTone(plan)}`}>
          {plan.guardianDubai ? '🇦🇪 ' : ''}{plan.label}
        </span>
        {!plan.isPaid && (
          <span style={{ fontSize: 11, color: 'var(--brand-accent)', fontWeight: 600 }}>
            Upgrade →
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
        {plan.coveredBySubscriber ? plan.detail : `Your plan · ${plan.detail}`}
      </div>
    </NavLink>
  );
}

// Shared with the mobile "Upload" action in the Today page's Health Journey
// card (see PatientTodayPage.jsx) — the topbar button hides on mobile below,
// so this same icon appears there instead rather than being redrawn.
export function UploadIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M17 8l-5-5-5 5" />
      <path d="M12 3v12" />
    </svg>
  );
}

function UploadReportButton() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="btn btn-accent topbar-upload"
      onClick={() => navigate('/patient/upload')}
      aria-label="Upload report"
      title="Upload report"
    >
      <UploadIcon />
      <span className="topbar-upload-label">Upload report</span>
    </button>
  );
}

export function PatientTopbar({ breadcrumbs = [], onMenuToggle }) {
  const { patient, profile, plan, logout, avatarUrl, refreshProfile } = usePatient();
  const navigate = useNavigate();

  const initials = patient
    ? `${patient.firstName?.[0] ?? ''}${patient.lastName?.[0] ?? ''}`.toUpperCase()
    : 'P';

  return (
    <header className="topbar">
      <button
        className="hamburger-btn"
        onClick={onMenuToggle}
        aria-label="Toggle navigation menu"
        type="button"
      >
        ☰
      </button>

      <div className="topbar-logo">
        <img src={previuLogo} alt="Previu" style={{ height: 34, width: 'auto' }} />
      </div>

      <nav className="topbar-breadcrumb">
        {breadcrumbs.map((crumb, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="crumb-sep">/</span>}
            {i === breadcrumbs.length - 1
              ? <span className="crumb-active">{crumb.label}</span>
              : <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => crumb.to && navigate(crumb.to)}
                >{crumb.label}</span>
            }
          </React.Fragment>
        ))}
      </nav>

      <div className="topbar-actions">
        <UploadReportButton />
        <NotificationBell role="PATIENT" patientId={patient?.id} plan={plan}
          appNotificationsOff={profile?.appNotificationPreference === false} />
        <ThemeToggle
          isDark={profile?.theme === 'DARK'}
          onChange={async next => { await api.put(`/patients/${patient.id}`, { theme: next }); refreshProfile(); }} />
        <AccountMenu
          initials={initials}
          avatarUrl={avatarUrl}
          name={`${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`.trim() || 'Patient'}
          role="Patient"
          secondary={patient?.phone || patient?.email}
          settingsTo="/patient/settings"
          onLogout={logout}
        >
          <PlanBlock plan={plan} />
        </AccountMenu>
      </div>
    </header>
  );
}

export function PatientSidebar({ isOpen, onClose }) {
  let lastSection = null;
  const location = useLocation();
  const { plan, careTeam, careNavigator } = usePatient();

  const navItems = PATIENT_NAV;

  return (
    <aside className={`sidebar${isOpen ? ' open' : ''}`}>
      {navItems.map((item) => {
        const showSection = item.section && item.section !== lastSection;
        if (item.section) lastSection = item.section;

        const matchesPath = (p) => location.pathname === p || location.pathname.startsWith(`${p}/`);
        const isActive = matchesPath(item.to) || (item.alsoActiveOn || []).some(matchesPath);

        return (
          <React.Fragment key={item.to}>
            {showSection && (
              <div className="sidebar-section">{item.section}</div>
            )}
            <NavLink
              to={item.to}
              className={`nav-item${isActive ? ' active' : ''}`}
              onClick={onClose}
            >
              <span className="nav-icon"><Icon name={item.icon} /></span>
              {item.label}
            </NavLink>
          </React.Fragment>
        );
      })}

      <NavigatorCard careTeam={careTeam} careNavigator={careNavigator} onClose={onClose} />

      {!plan?.loading && (
        <NavLink
          to="/patient/subscription"
          onClick={onClose}
          className="sidebar-plan-card"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className={`badge ${planChipTone(plan)}`}>
              {plan.guardianDubai ? '🇦🇪 ' : ''}{plan.label}
            </span>
            {!plan.isPaid && (
              <span style={{ fontSize: 11, color: 'var(--brand-accent)', fontWeight: 700 }}>
                Upgrade →
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 5 }}>
            {plan.coveredBySubscriber ? plan.detail : `Your plan · ${plan.detail}`}
          </div>
        </NavLink>
      )}
    </aside>
  );
}

// .chat-pane (index.css) gives chat screens a fixed height — 100dvh minus
// the topbar and .main-content's own padding — precisely so their composer
// stays anchored on screen while only the message list scrolls internally.
// That calc assumes this banner is the only thing that can appear above
// {children} inside .main-content; when it renders, it eats real vertical
// space the calc doesn't know to subtract, so a chat-pane page (Care Team,
// Ask Previu) ends up taller than the viewport by exactly the
// banner's height — pushing the composer that far below the fold, visible
// only by scrolling .main-content itself. Reported live: Care Team's
// message box was "not visible" for a patient with no gender set.
//
// Measuring the banner's own rendered height (it varies a lot: one line on
// desktop, two or three once it wraps on a narrow phone) and publishing it
// as --content-banner-h on .main-content lets .chat-pane's calc subtract
// exactly that much, whether the banner is showing or not — see its own
// rule in index.css. ResizeObserver keeps it correct across a resize/
// rotation without needing every chat-pane page to know this banner exists.
function MissingGenderBanner() {
  const { profile } = usePatient();
  const ref = useRef(null);
  const showBanner = !!profile && !profile.gender;

  useEffect(() => {
    const mainEl = document.querySelector('.main-content--patient');
    if (!mainEl) return undefined;

    if (!showBanner) {
      mainEl.style.setProperty('--content-banner-h', '0px');
      return undefined;
    }

    const el = ref.current;
    if (!el) return undefined;

    function update() {
      const height = el.getBoundingClientRect().height;
      const marginBottom = parseFloat(getComputedStyle(el).marginBottom) || 0;
      mainEl.style.setProperty('--content-banner-h', `${height + marginBottom}px`);
    }

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      mainEl.style.setProperty('--content-banner-h', '0px');
    };
  }, [showBanner]);

  if (!showBanner) return null;

  return (
    <div ref={ref} className="alert alert-info" style={{ marginBottom: 20 }}>
      <Icon name="alert" size={15} />
      <span>
        Your profile is missing your gender — some health assessments depend on it.{' '}
        <NavLink to="/patient/settings" style={{ fontWeight: 700, textDecoration: 'underline' }}>
          Update your profile →
        </NavLink>
      </span>
    </div>
  );
}

const GATE_EXEMPT_PATHS = ['/patient/settings', '/patient/onboarding'];

const GATE_API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

const gateDismissKey = patientId => `previu_gate_dismissed_${patientId}`;

function ProfileCompletionGate() {
  const { patient, profile } = usePatient();
  const location = useLocation();
  const [answeredCount, setAnsweredCount] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const patientId = patient?.id;

  useEffect(() => {
    if (!patientId) { setDismissed(false); return; }
    setDismissed(sessionStorage.getItem(gateDismissKey(patientId)) === '1');
  }, [patientId]);

  useEffect(() => {
    if (!patientId) { setAnsweredCount(null); return; }
    if (isSymptomLogComplete(answeredCount, profile)) return;
    let cancelled = false;
    fetch(`${GATE_API_BASE}/patients/${patientId}/symptoms`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('previu_token')}`,
      },
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled) return;
        const n = Array.isArray(data?.currentAnswers) ? data.currentAnswers.length : null;
        setAnsweredCount(n);
      })
      .catch(() => { if (!cancelled) setAnsweredCount(TOTAL_SYMPTOM_QUESTIONS); });
    return () => { cancelled = true; };
  }, [patientId, location.pathname, answeredCount, profile]);

  const status = profileGateStatus(profile, answeredCount);
  const onExemptPath = GATE_EXEMPT_PATHS.some(
    p => location?.pathname === p || location?.pathname?.startsWith(`${p}/`));

  if (!status.ready || status.complete || onExemptPath || dismissed) return null;

  const missing = [];
  if (!status.healthProfileComplete) missing.push('Health Profile');
  if (!status.symptomLogComplete) {
    missing.push(`Symptom Log (${status.answeredCount} of ${status.totalQuestions} answered)`);
  }

  function dismiss() {
    if (patientId) sessionStorage.setItem(gateDismissKey(patientId), '1');
    setDismissed(true);
  }

  return (
    <div className="patient-gate" role="dialog" aria-modal="true"
         aria-labelledby="patient-gate-title"
         onKeyDown={e => { if (e.key === 'Escape') dismiss(); }}>
      <div className="patient-gate-card" style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close — fill this in later"
          className="patient-gate-close"
        >
          ✕
        </button>
        <div className="patient-gate-icon" aria-hidden="true"><Icon name="stethoscope" size={30} /></div>
        <h2 id="patient-gate-title" className="patient-gate-title">
          One step before we can read your results
        </h2>
        <p className="patient-gate-body">
          Your lab results are interpreted against your health history and
          symptoms — without them we would be reading numbers with no context.
          Please complete the following:
        </p>
        <ul className="patient-gate-list">
          {missing.map(m => <li key={m}>{m}</li>)}
        </ul>
        <NavLink
          to={`/patient/settings#${firstIncompleteAnchor(status) || HEALTH_PROFILE_ANCHOR}`}
          className="btn btn-primary patient-gate-cta"
        >
          {status.healthProfileComplete
            ? 'Complete my symptom log →'
            : 'Complete my health profile →'}
        </NavLink>
        <p className="patient-gate-note">
          It takes a few minutes, and you only need to do it once. Everything
          else unlocks as soon as you are done — or close this and pick it up
          later from Settings.
        </p>
      </div>
    </div>
  );
}

// Mirrors DoctorBottomTabBar (Layout.js) exactly, sharing its .mobile-tab-*
// item styling — see index.css's "MOBILE BOTTOM TAB BAR" section.
function PatientBottomTabBar() {
  return (
    <nav className="patient-bottom-tabs" aria-label="Patient navigation">
      {PATIENT_BOTTOM_TABS.map(tab => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) =>
            `mobile-tab-item${isActive ? ' mobile-tab-item--active' : ''}`
          }
        >
          <span className="mobile-tab-icon" aria-hidden="true"><Icon name={tab.icon} /></span>
          <span className="mobile-tab-label">{tab.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function PatientAppShell({ children, breadcrumbs }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = useCallback(() => setMenuOpen(prev => !prev), []);
  const closeMenu  = useCallback(() => setMenuOpen(false), []);
  const { profile } = usePatient();

  return (
    <div className="app-shell" data-theme={profile?.theme === 'DARK' ? 'dark' : 'light'}>
      <PatientTopbar breadcrumbs={breadcrumbs} onMenuToggle={toggleMenu} />
      <PatientSidebar isOpen={menuOpen} onClose={closeMenu} />
      {menuOpen && (
        <div className="sidebar-overlay" onClick={closeMenu} aria-hidden="true" />
      )}
      <main className="main-content main-content--patient">
        <MissingGenderBanner />
        {children}
      </main>
      <ProfileCompletionGate />
      <PatientBottomTabBar />
    </div>
  );
}